import {
  AI_PROMPT_BY_ID_BASE_QUERY,
  AI_PROMPT_BY_ID_QUERY,
  AI_PROMPT_BY_TITLE_BASE_QUERY,
  AI_PROMPT_BY_TITLE_QUERY,
  AI_PROMPT_CHANNELS_PAGE_SIZE,
  AI_PROMPT_CHANNELS_QUERY,
  ALL_AI_PROMPTS_BASE_QUERY,
  ALL_AI_PROMPTS_QUERY,
} from '@src/gql/ai-prompts-gql';
import { LogManager } from '@src/utility/Logger';

import type { AIPrompt } from '../types.ts/ai_prompts';
import type { GraphQLClient } from 'graphql-request';

export class AIPromptRepository {
  private gqlClient: GraphQLClient;
  private logger = LogManager.getLogger('repository:AIPromptRepository');

  constructor(gqlClient: GraphQLClient) {
    this.gqlClient = gqlClient;
  }

  async getAllPrompts(): Promise<AIPrompt[]> {
    try {
      const response = (await this.gqlClient.request(ALL_AI_PROMPTS_QUERY)) as {
        allAiPrompts: AIPrompt[];
      };
      return response.allAiPrompts;
    } catch (err) {
      // Fallback to base query without model field (pre-migration compat)
      this.logger.warn(
        'Extended allAiPrompts query failed, falling back to base query',
        {
          error: err instanceof Error ? err.message : err,
        },
      );
      try {
        const response = (await this.gqlClient.request(
          ALL_AI_PROMPTS_BASE_QUERY,
        )) as {
          allAiPrompts: AIPrompt[];
        };
        return response.allAiPrompts;
      } catch (fallbackErr) {
        this.logger.error('Failed to load AI prompts (both queries failed)', {
          error:
            fallbackErr instanceof Error ? fallbackErr.message : fallbackErr,
        });
        return [];
      }
    }
  }

  /**
   * Maps prompt id -> distribution channel slug for every prompt that has one
   * (PLAT-11038). Prompts reaching the UI via the customer-type query use the
   * base fragment and carry no channel, so this is the join used to resolve the
   * preview for a selected prompt. Prompts with no channel are absent from the
   * map.
   *
   * Uses its own narrow query rather than `getAllPrompts()`, which is capped at
   * 100 while the prompt set is already larger — reusing it would silently drop
   * later prompts' channels. Any failure (including the field not existing
   * pre-migration) yields an empty map, so previews fall back to the default
   * instead of erroring.
   */
  async getPromptChannelSlugs(): Promise<Map<string, string>> {
    const slugsByPromptId = new Map<string, string>();
    try {
      const response = (await this.gqlClient.request(
        AI_PROMPT_CHANNELS_QUERY,
      )) as {
        allAiPrompts: {
          id: string;
          distributionChannel?: { slug: string } | null;
        }[];
      };
      const prompts = response.allAiPrompts ?? [];

      if (prompts.length >= AI_PROMPT_CHANNELS_PAGE_SIZE) {
        // Never truncate silently: past one page some prompts would lose their
        // channel and quietly fall back to the default preview.
        this.logger.warn(
          'AI prompt channel query filled a full page — prompts beyond it have no channel resolved and will use the default preview',
          { pageSize: AI_PROMPT_CHANNELS_PAGE_SIZE },
        );
      }

      for (const prompt of prompts) {
        const slug = prompt.distributionChannel?.slug;
        if (slug) slugsByPromptId.set(prompt.id, slug);
      }
    } catch (err) {
      // Expected before the migration adds `distribution_channel`.
      this.logger.warn(
        'AI prompt channel query failed — social previews will use the default channel',
        { error: err instanceof Error ? err.message : err },
      );
    }
    return slugsByPromptId;
  }

  async getPromptByTitle(title: string): Promise<AIPrompt | undefined> {
    try {
      const response = (await this.gqlClient.request(AI_PROMPT_BY_TITLE_QUERY, {
        title,
      })) as { aiPrompt: AIPrompt };
      return response.aiPrompt;
    } catch (err) {
      // Fallback to base query without model field (pre-migration compat)
      this.logger.warn(
        `Extended prompt query failed for "${title}", falling back to base query`,
        {
          error: err instanceof Error ? err.message : err,
        },
      );
      try {
        const response = (await this.gqlClient.request(
          AI_PROMPT_BY_TITLE_BASE_QUERY,
          {
            title,
          },
        )) as { aiPrompt: AIPrompt };
        return response.aiPrompt;
      } catch (fallbackErr) {
        this.logger.error(
          `Failed to load AI prompt "${title}" (both queries failed)`,
          {
            error:
              fallbackErr instanceof Error ? fallbackErr.message : fallbackErr,
          },
        );
        return undefined;
      }
    }
  }

  /**
   * Resolves a prompt by its DatoCMS record id. Prefer this over
   * getPromptByTitle whenever the caller already holds a record — prompt titles
   * are not unique across customer types ("Instagram Post" exists for several),
   * so a title lookup can return a different customer's prompt.
   */
  async getPromptById(id: string): Promise<AIPrompt | undefined> {
    try {
      const response = (await this.gqlClient.request(AI_PROMPT_BY_ID_QUERY, {
        id,
      })) as { aiPrompt: AIPrompt };
      return response.aiPrompt;
    } catch (err) {
      // Fallback to base query without model field (pre-migration compat)
      this.logger.warn(
        `Extended prompt query failed for id "${id}", falling back to base query`,
        {
          error: err instanceof Error ? err.message : err,
        },
      );
      try {
        const response = (await this.gqlClient.request(
          AI_PROMPT_BY_ID_BASE_QUERY,
          {
            id,
          },
        )) as { aiPrompt: AIPrompt };
        return response.aiPrompt;
      } catch (fallbackErr) {
        this.logger.error(
          `Failed to load AI prompt id "${id}" (both queries failed)`,
          {
            error:
              fallbackErr instanceof Error ? fallbackErr.message : fallbackErr,
          },
        );
        return undefined;
      }
    }
  }
}
